import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useLanguage } from 'hooks';
import { styled } from 'netline-ui';
import services from 'services';
import { PreferenceSelectorOption } from 'types';

import MobileHeader from '../MobileHeader/MobileHeader';
import { PreferencesMenuClose } from '../MobilePreferences';
import MobileRadioGroupPreference from '../MobileRadioGroupPreference/MobileRadioGroupPreference';
import PreferencesPopup, { PreferencesPopupType } from '../PreferencesPopup/PreferencesPopup';

import styles, { mobileLanguageSelectorClasses } from './MobileLanguageSelector.styles';

const MobileLanguageSelectorRoot = styled('div', { name: mobileLanguageSelectorClasses.$name })(styles);

const MAX_LANGUAGE_WITHOUT_POPUP = 3;
const MobileLanguageSelector = React.forwardRef<HTMLDivElement, PreferencesMenuClose>((_, ref) => {
  const { t } = useTranslation();

  const popupRef = useRef<PreferencesPopupType>(null);

  const { availableLanguages, selectedLanguage } = useLanguage();

  const languageOptions: PreferenceSelectorOption[] | null = useMemo(
    () =>
      availableLanguages?.map((language: string) => ({ label: t(`languages.${language}`), value: language })) || null,
    [availableLanguages, t],
  );

  const closePopup = useCallback(() => {
    if (popupRef.current?.closePopup) {
      popupRef.current.closePopup();
    }
  }, []);

  const handleLanguageSelect = useCallback(
    (event) => {
      services.appFrame.language.setLanguage(event.target.value);
      closePopup();
    },
    [closePopup],
  );
  if (!languageOptions) {
    return null;
  }

  if (availableLanguages!.length <= MAX_LANGUAGE_WITHOUT_POPUP) {
    return (
      <MobileRadioGroupPreference
        data-testid="MobileLanguageSelector-no-popup"
        title={t('languages.select_language')}
        value={selectedLanguage as string}
        items={languageOptions}
        onChange={handleLanguageSelect}
      />
    );
  }

  return (
    <MobileLanguageSelectorRoot data-testid="MobileLanguageSelector-root" ref={ref}>
      <PreferencesPopup title={t('languages.select_language')} ref={popupRef}>
        <MobileHeader
          title={t('languages.select_language')}
          onClose={closePopup}
          data-testid="MobileLanguageSelector-popup-header"
        />
        <div className={mobileLanguageSelectorClasses.content} data-testid="MobileLanguageSelector-popup-content">
          <MobileRadioGroupPreference
            data-testid="MobileLanguageSelector-with-popup"
            value={selectedLanguage as string}
            items={languageOptions}
            onChange={handleLanguageSelect}
          />
        </div>
      </PreferencesPopup>
    </MobileLanguageSelectorRoot>
  );
});

export default MobileLanguageSelector;
